import './styles.scss'

import { useTranslation } from 'react-i18next'
import { CheckCircleFilled } from '@ant-design/icons'
import { Form, Modal } from 'antd'

import CustomButton from '@/components/CustomButton'
import { fapiCustomerService } from '@/services/fapi'
import { useAppSelector } from '@/store/hook'
import { selectUserType } from '@/store/slices/authenticationSlice'
import useAsync from '@/hooks/useAsync'
import usePermission from '@/hooks/usePermission'
import { MODULE_SERVICE_RIGHTS } from '@/libs/constants/modulerights'
import { USER_TYPES } from '@/libs/constants/user'

import { CONTACT_TYPES, EnquiryForm } from './components/EnquiryForm'

const { useForm } = Form

interface EnquiryModalProps {
  onClose: (...args: unknown[]) => unknown
  materialNumber: string
  materialName: string
  companyName?: string
  phoneNumber?: string
}

const EnquiryModal = ({
  onClose,
  materialNumber,
  materialName,
  companyName = 'N/A',
  phoneNumber = '',
  email,
}: EnquiryModalProps) => {
  const { hasPermission: canCreateProductRequest } = usePermission(
    MODULE_SERVICE_RIGHTS.CREATE_PRODUCT_REQUEST,
  )
  const userType = useAppSelector(selectUserType)
  const { t } = useTranslation()
  const [form] = useForm()

  const submitProductEnquiry = async (parameters) => fapiCustomerService.submitProductEnquiry(parameters)

  const { execute, status, error } = useAsync(submitProductEnquiry, false)

  const isSuccess = status === 'success'

  const isLoading = status === 'pending'

  const shouldShowInput = !isSuccess

  const handleRequestProductEnquiry = async ({
    contactType: type,
    ...parameters
  }) => {
    const body = { ...parameters, serviceContact: !!parameters.serviceContact }
    if (type === 'contactEmail') {
      delete body.contactPhone
    } else if (type === 'contactPhone') {
      delete body.contactEmail
    }
    execute(body)
  }

  const handleSubmit = async () => {
    try {
      if (canCreateProductRequest) {
        await form.validateFields()
        const values = form.getFieldsValue()

        const bodyRequest = {
          productNumber: materialNumber,
          productName: materialName,
          ...values,
        }

        handleRequestProductEnquiry(bodyRequest)
      }
    } catch (error) {
      console.error('Error submitting product enquiry:', error)
    }

    return {}
  }

  return (
    <Modal
      open
      className="enquiry-modal"
      closable={false}
      footer={null}
      width={570}
    >
      <h3 className="enquiry-modal__header">{t('enquiryModal.header')}</h3>
      {isSuccess
        ? (
          <div className="enquiry-modal-success">
            <CheckCircleFilled />
            <span className="enquiry-modal-success__message">
              {t('enquiryModal.successfullMessage')}
            </span>
            <CustomButton onClick={onClose}>
              {t('enquiryModal.button.backToDetails')}
            </CustomButton>
          </div>
        )
        : null}
      {shouldShowInput
        ? (
          <>
            <div className="enquiry-modal__description">
              {t('enquiryModal.description')}
            </div>

            {error
              ? (
                <div className="enquiry-modal-error">
                  {t('enquiryModal.errorMessage')}
                </div>
              )
              : null}

            <EnquiryForm
              form={form}
              isLoading={isLoading}
              initialValues={{
                companyName,
                contactType: CONTACT_TYPES[0].value,
                contactEmail: userType !== USER_TYPES.TECHNICAL_USER ? email : '',
                contactPhone: userType !== USER_TYPES.TECHNICAL_USER ? phoneNumber : '',
              }}
            />

            <div className="enquiry-modal__footer">
              <CustomButton disabled={isLoading} type="ghost" onClick={onClose}>
                {t('enquiryModal.button.cancel')}
              </CustomButton>
              <CustomButton
                disabled={!canCreateProductRequest}
                loading={isLoading}
                onClick={handleSubmit}
              >
                {t('enquiryModal.button.pleaseContactme')}
              </CustomButton>
            </div>
          </>
        )
        : null}
    </Modal>
  )
}

export default EnquiryModal
